import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { useCart } from "@/contexts/CartContext";
import { formatINR, productImg } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => { setOpen(false); navigate("/checkout"); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md p-0 flex flex-col border-l border-[#fde0e8]"
        style={{ background: "var(--petal)" }}
        onPointerDownOutside={(e) => {
          if (e.target && e.target.closest?.('[data-testid="open-cart-button"]')) {
            e.preventDefault();
          }
        }}
      >
        <SheetHeader className="px-6 py-6 border-b border-[#fde0e8] bg-white">
          <SheetTitle className="font-serif text-[1.6rem] font-semibold text-[var(--ink)]">Your bag</SheetTitle>
          <p className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">
            {items.length} item{items.length !== 1 ? "s" : ""}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center pt-20">
              <div className="w-20 h-20 rounded-full bg-white grid place-items-center shadow-sm mb-5">
                <ShoppingBag className="w-8 h-8" style={{ color: "var(--bloom)" }} />
              </div>
              <h3 className="font-serif text-[1.4rem] font-semibold">Your bag is empty</h3>
              <p className="text-sm text-[var(--ink-soft)] mt-1 max-w-xs">Pick a design. Your future nails will thank you.</p>
              <button
                onClick={() => { setOpen(false); navigate("/shop"); }}
                data-testid="cart-empty-shop-btn"
                className="btn btn-primary mt-6"
              >
                Browse designs ♡
              </button>
            </div>
          ) : (
            items.map((it) => (
              <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="bg-white rounded-2xl p-3 flex gap-3 shadow-[0_4px_14px_-8px_rgba(38,12,24,.18)]">
                <img src={productImg(it.image)} alt={it.name} className="w-20 h-20 rounded-xl object-cover" />
                <div className="flex-1">
                  <div className="font-serif font-semibold leading-tight">{it.name}</div>
                  <div className="font-bold mt-1" style={{ color: "var(--bloom)" }}>{formatINR(it.price)}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => updateQty(it.product_id, it.qty - 1)} data-testid={`qty-dec-${it.product_id}`} className="w-7 h-7 rounded-full bg-[var(--petal)] hover:bg-[var(--rose)] grid place-items-center">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span data-testid={`qty-${it.product_id}`} className="w-6 text-center font-bold text-sm">{it.qty}</span>
                    <button onClick={() => updateQty(it.product_id, it.qty + 1)} data-testid={`qty-inc-${it.product_id}`} className="w-7 h-7 rounded-full bg-[var(--petal)] hover:bg-[var(--rose)] grid place-items-center">
                      <Plus className="w-3 h-3" />
                    </button>
                    <button onClick={() => removeItem(it.product_id)} data-testid={`remove-${it.product_id}`} className="ml-auto" style={{ color: "var(--bloom)" }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <SheetFooter className="px-6 py-5 border-t border-[#fde0e8] bg-white flex-col gap-3">
            <div className="flex justify-between w-full font-bold text-[var(--ink)]">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{formatINR(subtotal)}</span>
            </div>
            <p className="text-xs text-[var(--ink-soft)] w-full">Free shipping over ₹699 · taxes calculated at checkout</p>
            <button onClick={goCheckout} data-testid="checkout-button" className="btn btn-primary w-full justify-center">
              Checkout
            </button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};
